import React, { useCallback, useRef, useState } from 'react';
import { Pressable } from 'react-native';
import { Feather } from '@expo/vector-icons';

import Text from '../../../components/text/Text';
import { Sheet } from '../../../design/components';
import { useTheme } from '../../../theme';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import SelectField from './SelectField';

// Long lists (e.g. times in 15-minute steps) get the tall sheet.
const FULL_HEIGHT_OPTION_COUNT = 8;
// Rows shown above the selected option when the sheet opens scrolled to it.
const ROWS_ABOVE_SELECTED = 2;

/**
 * OptionSelect: SelectField whose sheet lists the options; picking one closes the sheet.
 * Props: label, options ([{ value, label }]), value, onChange(value), placeholder, disabled, style,
 * showLabel (SelectField)
 */
const OptionSelect = ({
	label,
	options,
	value,
	onChange,
	placeholder,
	disabled,
	showLabel,
	style,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const [isOpen, setIsOpen] = useState(false);
	const scrollRef = useRef(null);
	const selectedOption = options.find(option => option.value === value);

	const open = useCallback(() => setIsOpen(true), []);
	const close = useCallback(() => setIsOpen(false), []);
	const select = useCallback((nextValue) => {
		onChange(nextValue);
		setIsOpen(false);
	}, [onChange]);
	// Open long lists at the current choice (e.g. 9am) instead of the top of the list.
	const scrollToSelected = useCallback((event) => {
		const { y, height } = event.nativeEvent.layout;
		scrollRef.current?.scrollTo({ y: Math.max(0, y - (ROWS_ABOVE_SELECTED * height)), animated: false });
	}, []);

	return (
		<>
			<SelectField
				label={label}
				valueLabel={selectedOption?.label}
				placeholder={placeholder}
				onPress={open}
				disabled={disabled}
				showLabel={showLabel}
				style={style}
			/>
			<Sheet
				isVisible={isOpen}
				onClose={close}
				title={label}
				fullHeight={options.length > FULL_HEIGHT_OPTION_COUNT}
				scrollRef={scrollRef}
			>
				{options.map((option) => {
					const isSelected = option.value === value;

					return (
						<Pressable
							key={String(option.value)}
							onPress={() => select(option.value)}
							onLayout={isSelected ? scrollToSelected : undefined}
							accessibilityRole="button"
							accessibilityLabel={option.label}
							accessibilityState={{ selected: isSelected }}
							style={({ pressed }) => [styles.option, isSelected && styles.optionSelected, pressed && styles.pressed]}
						>
							<Text style={[styles.optionLabel, isSelected && styles.optionLabelSelected]}>{option.label}</Text>
							{isSelected && <Feather name="check" size={20} color={colors.accent} />}
						</Pressable>
					);
				})}
			</Sheet>
		</>
	);
};

const createStyles = ({ ui: { colors, radius, space, touch, type } }) => ({
	option: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: space.md,
		minHeight: touch.row,
		paddingHorizontal: 14,
		borderRadius: radius.card,
		borderWidth: 1,
		borderColor: colors.raisedBorder,
		backgroundColor: colors.raised,
	},
	optionSelected: {
		borderColor: colors.currentBorder,
		backgroundColor: colors.current,
	},
	pressed: {
		opacity: 0.85,
	},
	optionLabel: {
		...type.rowText,
		color: colors.text,
		flex: 1,
	},
	optionLabelSelected: {
		...type.rowTitleSemibold,
	},
});

export default OptionSelect;
